import * as XLSX from "xlsx";

export const MAX_PIPELINE_ROWS = 1000;

export const PIPELINE_IMPORT_FIELDS = [
  { key: "stage_name", label: "Stage name", required: true, group: "Stage" },
  { key: "stage_key", label: "Stage key", group: "Stage" },
  { key: "stage_order", label: "Stage order", group: "Stage" },
  { key: "stage_description", label: "Stage description", group: "Stage" },
  { key: "stage_probability", label: "Win probability", group: "Stage" },
  { key: "stage_is_closed", label: "Closed stage", group: "Stage" },
  { key: "stage_is_active", label: "Active stage", group: "Stage" },
  { key: "node_name", label: "Node / action name", group: "Workflow" },
  { key: "node_key", label: "Node key", group: "Workflow" },
  { key: "node_order", label: "Node order", group: "Workflow" },
  { key: "node_description", label: "Node description", group: "Workflow" },
  { key: "node_type", label: "Node type", group: "Workflow" },
  { key: "node_can_skip", label: "Can skip", group: "Workflow" },
  { key: "next_node_key", label: "Next node", group: "Connection" },
  { key: "transition_label", label: "Branch label", group: "Connection" },
];

const ALIASES = {
  stage_name: ["stage", "stage name", "pipeline stage", "status"],
  stage_key: ["stage key", "stage id"],
  stage_order: ["stage order", "stage position", "stage sequence"],
  stage_description: ["stage description", "stage details"],
  stage_probability: ["probability", "stage probability", "win probability"],
  stage_is_closed: ["closed", "is closed", "closed stage"],
  stage_is_active: ["active", "is active", "active stage"],
  node_name: ["node", "node name", "activity", "action", "task"],
  node_key: ["node key", "node id", "activity key", "action key"],
  node_order: ["node order", "activity order", "action order", "node position"],
  node_description: ["node description", "activity description", "action description"],
  node_type: ["node type", "activity type", "action type", "type"],
  node_can_skip: ["can skip", "skippable", "optional"],
  next_node_key: ["next node", "next node key", "next activity", "next action"],
  transition_label: ["transition label", "branch label", "path label", "outcome"],
};

const normalizeHeader = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, " ")
  .replace(/[^a-z0-9 ]+/g, "")
  .replace(/\s+/g, " ");

function dedupeHeaders(values) {
  const seen = new Set();

  return values.map((value, index) => {
    const base = String(value || "").trim() || `column_${index + 1}`;
    let name = base;
    let suffix = 2;

    while (seen.has(name)) name = `${base} (${suffix++})`;
    seen.add(name);
    return name;
  });
}

function worksheetToRows(worksheet) {
  const matrix = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });

  if (!matrix.length) throw new Error("This source is empty.");

  const columns = dedupeHeaders(matrix[0]);
  const allRows = matrix.slice(1)
    .map((values) => {
      const row = {};
      columns.forEach((column, index) => {
        row[column] = values[index] == null ? "" : String(values[index]).trim();
      });
      return row;
    })
    .filter((row) => Object.values(row).some(Boolean));

  if (!columns.length) throw new Error("Add a header row with your column names.");
  if (!allRows.length) throw new Error("Add at least one pipeline row below the header.");

  return {
    columns,
    rows: allRows.slice(0, MAX_PIPELINE_ROWS),
    totalRows: allRows.length,
    truncated: allRows.length > MAX_PIPELINE_ROWS,
  };
}

function firstWorksheet(workbook) {
  const name = workbook.SheetNames?.[0];
  if (!name || !workbook.Sheets?.[name]) throw new Error("No worksheet was found.");
  return workbook.Sheets[name];
}

export async function parsePipelineFile(file) {
  if (!(file instanceof File)) throw new Error("Choose a spreadsheet file first.");
  if (file.size > 10 * 1024 * 1024) throw new Error("Choose a file smaller than 10 MB.");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  return worksheetToRows(firstWorksheet(workbook));
}

export function parsePastedPipelineRows(value) {
  const text = String(value || "").trim();
  if (!text) throw new Error("Paste a header row and at least one pipeline row.");
  const workbook = XLSX.read(text, { type: "string" });
  return worksheetToRows(firstWorksheet(workbook));
}

export function suggestPipelineMapping(columns = []) {
  const mapping = {};
  const used = new Set();

  Object.entries(ALIASES).forEach(([field, aliases]) => {
    const match = columns.find((column) => !used.has(column) && aliases.includes(normalizeHeader(column)));
    if (match) {
      mapping[field] = match;
      used.add(match);
    }
  });

  return mapping;
}

export function summarizePipelineRows(rows = [], mapping = {}) {
  const stageColumn = mapping.stage_name;
  const nodeColumn = mapping.node_name;
  const nodeKeyColumn = mapping.node_key;
  const stages = new Set();
  const nodes = new Set();
  let explicitConnections = 0;

  rows.forEach((row) => {
    const stage = stageColumn ? String(row?.[stageColumn] || "").trim().toLowerCase() : "";
    if (stage) stages.add(stage);
    const node = nodeColumn ? String(row?.[nodeColumn] || "").trim().toLowerCase() : "";
    const nodeKey = nodeKeyColumn ? String(row?.[nodeKeyColumn] || "").trim().toLowerCase() : "";
    if (node) nodes.add(nodeKey || `${stage}:${node}`);

    if (mapping.next_node_key) {
      explicitConnections += String(row?.[mapping.next_node_key] || "")
        .split(/[,|;]+/)
        .filter((value) => value.trim()).length;
    }
  });

  return {
    stages: stages.size,
    nodes: nodes.size,
    connections: mapping.next_node_key
      ? explicitConnections
      : Math.max(0, nodes.size - 1),
    automaticConnections: !mapping.next_node_key,
  };
}
